import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../api/axios";

const LABELS = { hiring: "Hiring", layoffs: "Layoffs", campus: "Campus Hiring", government: "Govt Jobs" };
const GRADIENT = { backgroundImage: "linear-gradient(90deg,#7C3AED,#DB2777)" };

export function timeAgo(date) {
  const mins = Math.floor((Date.now() - new Date(date)) / 60000);
  if (mins < 60) return `${Math.max(mins, 1)}m ago`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
  return `${Math.floor(mins / 1440)}d ago`;
}

/* ---------------- popup shown to visitors who are not logged in ---------------- */
function LoginPrompt({ from, onClose }) {
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#1E0F3D]/60 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Login required"
    >
      <div
        className="relative w-full max-w-md bg-white rounded-3xl border border-purple-100 shadow-[0_40px_80px_-30px_rgba(124,58,237,0.5)] p-8 text-center"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 w-8 h-8 rounded-full text-gray-400 hover:bg-[#F5F0FA] hover:text-[#7C3AED] transition"
        >
          ✕
        </button>

        <div className="w-14 h-14 mx-auto mb-5 rounded-2xl text-white flex items-center justify-center shadow-lg shadow-[#7C3AED]/25" style={GRADIENT}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7" aria-hidden="true">
            <rect x="4" y="11" width="16" height="10" rx="2" />
            <path d="M8 11V7a4 4 0 018 0v4" />
          </svg>
        </div>

        <h3 className="text-xl font-bold text-[#1E0F3D] mb-2">Login or Register to read the full news</h3>
        <p className="text-sm text-gray-500 leading-relaxed mb-6">
          Create a free PlanB Jobs account or log in to read the complete story and get the latest hiring updates.
        </p>

        <button
          type="button"
          onClick={() => navigate("/login", { state: { from } })}
          className="w-full text-white px-8 py-3.5 rounded-xl font-semibold text-sm shadow-[0_10px_30px_-8px_rgba(124,58,237,0.6)] hover:opacity-90 transition"
          style={GRADIENT}
        >
          Login
        </button>

        <button
          type="button"
          onClick={() => navigate("/register", { state: { from } })}
          className="w-full mt-3 px-8 py-3.5 rounded-xl font-semibold text-sm text-[#7C3AED] bg-[#F5F0FA] hover:bg-[#EBDDFB] transition"
        >
          Register free
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------ card ------------------------------------ */
export function NewsCard({ n }) {
  const [showPrompt, setShowPrompt] = useState(false);

  const handleClick = (e) => {
    if (!localStorage.getItem("token")) {
      e.preventDefault();          // don't open the detail page
      setShowPrompt(true);         // show login / register popup
    }
  };

  return (
    <>
      <Link
        to={`/news/${n.id}`}
        onClick={handleClick}
        className="bg-white rounded-2xl overflow-hidden border border-purple-100 hover:shadow-lg transition flex flex-col"
      >
        {n.image_url && <img src={n.image_url} alt="" loading="lazy" className="h-40 w-full object-cover" />}
        <div className="p-4 flex flex-col flex-1">
          <span className="text-xs font-semibold text-[#7C3AED]">{LABELS[n.category]}</span>
          <h3 className="mt-1 font-semibold text-[#1E0F3D] line-clamp-2">{n.title}</h3>
          <p className="mt-2 text-sm text-gray-600 line-clamp-3">{n.description}</p>
          <p className="mt-auto pt-3 text-xs text-gray-500">{n.source_name} · {timeAgo(n.published_at)}</p>
        </div>
      </Link>

      {showPrompt && <LoginPrompt from={`/news/${n.id}`} onClose={() => setShowPrompt(false)} />}
    </>
  );
}

/* ------------------------------- homepage section ------------------------------- */
export default function JobNewsSection({ limit = 6 }) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get(`/news?per_page=${limit}`).then(r => setItems(r.data?.data ?? [])).catch(() => {});
  }, [limit]);

  if (!items.length) return null;

  return (
    <section className="py-12 bg-[#F5F0FA]">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-[#1E0F3D]">Latest Jobs & Career News</h2>
          <Link to="/news" className="text-sm font-semibold text-[#DB2777]">View all →</Link>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {items.map(n => <NewsCard key={n.id} n={n} />)}
        </div>
      </div>
    </section>
  );
}